/**
 * Assets panel — bottom-left quadrant.
 * Shows assets by category with current value and monthly cashflow.
 * Income-producing assets show a green badge linking them to passive income.
 */

import { useState } from 'react';
import type { Asset } from '../../types/cashflow';
import { formatCurrency } from '../../utils/cashflowCalc';

interface Props {
  assets: Asset[];
}

const CATEGORY_LABELS: Record<Asset['category'], string> = {
  real_estate: 'Real Estate',
  paper: 'Paper Assets',
  business: 'Business',
  cash: 'Cash',
  other: 'Other',
};

const CATEGORY_ICONS: Record<Asset['category'], string> = {
  real_estate: '🏠',
  paper: '📈',
  business: '🏢',
  cash: '💰',
  other: '📦',
};

export default function AssetPanel({ assets }: Props) {
  const [collapsed, setCollapsed] = useState(false);

  const grouped = assets.reduce<Record<string, Asset[]>>((acc, a) => {
    if (!acc[a.category]) acc[a.category] = [];
    acc[a.category].push(a);
    return acc;
  }, {});

  const totalValue = assets.reduce((sum, a) => sum + a.current_value, 0);
  const totalCashflow = assets.reduce((sum, a) => sum + a.monthly_cashflow, 0);

  return (
    <div className="card" style={{ flex: 1, minWidth: 280 }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)', cursor: 'pointer' }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <h3 style={{ color: '#4299e1', margin: 0, fontSize: '1rem', fontWeight: 700 }}>
          ▲ ASSETS
        </h3>
        <span style={{ color: 'var(--color-text-secondary)', fontSize: '1.1rem' }}>
          {formatCurrency(totalValue)} {collapsed ? '▸' : '▾'}
        </span>
      </div>

      {!collapsed && (
        <div>
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} style={{ marginBottom: 'var(--spacing-md)' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                {CATEGORY_ICONS[cat as Asset['category']]} {CATEGORY_LABELS[cat as Asset['category']]}
              </div>
              {items.map((a) => (
                <div key={a.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{a.name}</div>
                    {a.monthly_cashflow > 0 && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-success)', marginTop: 2 }}>
                        ↑ Generates {formatCurrency(a.monthly_cashflow)}/mo passive income
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600 }}>{formatCurrency(a.current_value)}</div>
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div style={{ borderTop: '2px solid var(--color-border)', paddingTop: 'var(--spacing-sm)', marginTop: 'var(--spacing-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
              <span>Total Value</span>
              <span style={{ color: '#4299e1' }}>{formatCurrency(totalValue)}</span>
            </div>
            {totalCashflow > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginTop: 4 }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>Total Monthly Income</span>
                <span style={{ color: 'var(--color-success)' }}>{formatCurrency(totalCashflow)}/mo</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
